import React, { useState, useEffect, useCallback } from 'react';
import { X, Info, Shield } from 'lucide-react';
import { useCreateField, getAvailableFieldTypes, type CustomField } from '../../../services/queries/fieldQueries';
import { useObjects } from '../../../services/queries/objectQueries';
import { FieldSecurityManager } from '../object-manager/FieldSecurityManager';
import { useToast } from '../../../contexts/ToastContext';
import { useSetupChanges } from '../../../hooks/useSetupChanges';
import { StandardButton, StandardInput, StandardTextarea, StandardSelect, StandardCheckbox } from '../../standard';
import { getCustomSuffix } from '../../../config/gobodoConfig';
import { AsyncSearchableSelect } from '../../modals/AsyncSearchableSelect';

interface CreateFieldModalProps {
  objectId: string;
  objectLabel: string;
  objectName: string;  // API name like "Client__c"
  isOpen: boolean;
  onClose: () => void;
  field?: any;  // For editing existing fields
}

export const CreateFieldModal: React.FC<CreateFieldModalProps> = ({
  objectId,
  objectLabel,
  objectName,
  isOpen,
  onClose,
  field
}) => {
  const [step, setStep] = useState(1); // 1: Choose type, 2: Configure field
  const [fieldType, setFieldType] = useState<CustomField['fieldType'] | null>(null);

  const [formData, setFormData] = useState({
    id: field?.id || '',  // Track if editing existing field
    fieldLabel: field?.fieldLabel || '',
    fieldName: field?.fieldName || '',
    description: field?.description || '',
    helpText: field?.helpText || '',
    required: field?.required || false,
    unique: field?.unique || false,
    defaultValue: field?.defaultValue || '',
    length: field?.length || 255,
    precision: field?.precision || 10,
    scale: field?.scale || 2,
    picklistValues: field?.picklistValues || ([] as string[]),
    lookupObject: field?.lookupObject || '',
    autoNumberFormat: field?.autoNumberFormat || 'AUTO-{0000}',
    autoNumberStarting: field?.autoNumberStarting || 1,
    formulaReturnType: field?.formulaReturnType || 'text',
    formulaExpression: field?.formulaExpression || '',
    rollupChildObject: field?.rollupChildObject || '',
    rollupChildField: field?.rollupChildField || '',
    rollupOperation: field?.rollupOperation || 'SUM',
    rollupFilter: field?.rollupFilter || '',
    masterDetailCascadeDelete: field?.masterDetailCascadeDelete ?? true,
    masterDetailShareAccess: field?.masterDetailShareAccess || 'ReadOnly',
    isPII: field?.isPII || false,
    fieldLevelSecurity: field?.fieldLevelSecurity || {
      visibleTo: ['all'] as string[],
      editableBy: ['admin'] as string[]
    }
  });

  // Update default length when fieldType changes
  useEffect(() => {
    if (fieldType && !field?.id) { // Only update if creating new field, not editing
      let defaultLength: number;
      
      if (fieldType === 'textarea') {
        defaultLength = 1000;
      } else if (fieldType === 'longtextarea') {
        defaultLength = 32000;
      } else {
        // For text fields and other types, default to 255
        defaultLength = 255;
      }
      
      // Update length to the appropriate default for the selected field type
      setFormData(prev => ({ ...prev, length: defaultLength }));
    }
  }, [fieldType, field?.id]);

  const createFieldMutation = useCreateField();
  const { success, error } = useToast();
  const { logChange } = useSetupChanges();

  const fieldTypes = getAvailableFieldTypes();
  
  // Fetch available objects for lookup field dropdown
  const { data: availableObjects = [], isLoading: isLoadingObjects } = useObjects({ isActive: true });
  
  // Filter out current object (can't create lookup to itself)
  const lookupTargetObjects = availableObjects.filter(obj => obj.id !== objectId);

  // Async search for master (parent) object / related object - same pattern as lookup record search
  const searchObjectTypes = useCallback(async (query: string): Promise<Array<{ value: string; label: string }>> => {
    const q = query.toLowerCase().trim();
    const list = lookupTargetObjects;
    if (!q) return list.map(o => ({ value: o.apiName, label: o.label }));
    return list
      .filter(o => o.label.toLowerCase().includes(q) || o.apiName.toLowerCase().includes(q))
      .map(o => ({ value: o.apiName, label: o.label }));
  }, [lookupTargetObjects]);

  // Auto-generate API name from label
  const handleLabelChange = (label: string) => {
    setFormData(prev => ({
      ...prev,
      fieldLabel: label,
      fieldName: label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
    }));
  };

  const handleSubmit = async () => {
    if (!fieldType) return;

    // Validate field label
    if (!formData.fieldLabel || formData.fieldLabel.trim() === '') {
      error('Field label is required', 'Please enter a label for this field');
      return;
    }

    // Validate field API name
    if (!formData.fieldName || formData.fieldName.trim() === '') {
      error('Field API name is required', 'Please enter an API name for this field');
      return;
    }

    // Validate picklist values if picklist type
    if ((fieldType === 'picklist' || fieldType === 'multipicklist') && formData.picklistValues.filter((v: string) => v.trim()).length === 0) {
      error('Picklist values required', 'Please enter at least one picklist value');
      return;
    }

    // Validate lookup object if lookup/masterdetail type
    if ((fieldType === 'lookup' || fieldType === 'masterdetail') && !formData.lookupObject) {
      error('Related object required', 'Please select a related object for this lookup field');
      return;
    }

    try {
      // Filter out empty picklist values before saving
      const cleanedFormData = {
        ...formData,
        picklistValues: formData.picklistValues.filter((v: string) => v.trim())
      };

      await createFieldMutation.mutateAsync({
        objectId,
        fieldType,
        ...cleanedFormData,
        isActive: true
      });

      // Track field creation
      logChange({
        action: 'created',
        entityType: 'field',
        entityName: formData.fieldLabel,
        targetName: `${objectLabel} object`,
        description: `created field ${formData.fieldLabel} on ${objectLabel} object`,
        performedBy: 'Admin'
      });

      // Success toast
      success('Field created successfully!', `${formData.fieldLabel} has been added to ${objectLabel}`);

      // Close modal and reset
      onClose();
      resetForm();
    } catch (err: any) {
      console.error('Failed to create field:', err);
      error('Failed to create field', err?.message || 'An error occurred while creating the field. Please try again.');
    }
  };

  const resetForm = () => {
    setStep(1);
    setFieldType(null);
    setFormData({
      id: '',
      fieldLabel: '',
      fieldName: '',
      description: '',
      helpText: '',
      required: false,
      unique: false,
      defaultValue: '',
      length: 255, // Will be updated by useEffect when fieldType is selected
      precision: 10,
      scale: 2,
      picklistValues: [],
      lookupObject: '',
      autoNumberFormat: 'AUTO-{0000}',
      autoNumberStarting: 1,
      formulaReturnType: 'text',
      formulaExpression: '',
      rollupChildObject: '',
      rollupChildField: '',
      rollupOperation: 'SUM',
      rollupFilter: '',
      masterDetailCascadeDelete: true,
      masterDetailShareAccess: 'ReadOnly',
      isPII: false,
      fieldLevelSecurity: {
        visibleTo: ['all'],
        editableBy: ['admin']
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {step === 1 ? 'Choose Field Type' : 'Configure Field'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {step === 1
                ? `Select the type of field to add to ${objectLabel}`
                : `Set up your ${fieldType} field`
              }
            </p>
          </div>
          <StandardButton
            variant="ghost"
            onClick={() => {
              onClose();
              resetForm();
            }}
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </StandardButton>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 ? (
            // Step 1: Choose Field Type (Grouped by Category)
            <div className="space-y-6">
              {['text', 'number', 'datetime', 'picklist', 'relationship', 'calculated', 'contact', 'location', 'advanced', 'other'].map(category => {
                const categoryFields = fieldTypes.filter(t => t.category === category);
                if (categoryFields.length === 0) return null;

                const categoryLabels: Record<string, string> = {
                  text: 'Text Fields',
                  number: 'Number Fields',
                  datetime: 'Date & Time Fields',
                  picklist: 'Picklist Fields',
                  relationship: 'Relationship Fields',
                  calculated: 'Calculated Fields',
                  contact: 'Contact Information',
                  location: 'Location',
                  advanced: 'Advanced',
                  other: 'Other'
                };

                return (
                  <div key={category}>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                      {categoryLabels[category]}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {categoryFields.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => {
                            setFieldType(type.value as CustomField['fieldType']);
                            setStep(2);
                          }}
                          disabled={type.badge === 'Soon'}
                          className={`text-left p-4 border-2 rounded-lg transition-colors relative ${
                            type.badge === 'Soon'
                              ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 cursor-not-allowed opacity-60'
                              : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                          }`}
                        >
                          {type.badge && (
                            <span className="absolute top-2 right-2 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-medium rounded">
                              {type.badge}
                            </span>
                          )}
                          <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{type.label}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">{type.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Step 2: Configure Field
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Basic Information</h3>

                <div className="space-y-4">
                  {/* Field Label */}
                  <StandardInput
                    label="Field Label"
                    required
                    value={formData.fieldLabel}
                    onChange={(e) => handleLabelChange(e.target.value)}
                    placeholder="e.g., Annual Contract Value"
                  />

                  {/* Field Name (API Name) */}
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <StandardInput
                          label="Field Name (API)"
                          required
                          value={formData.fieldName}
                          onChange={(e) => setFormData(prev => ({ ...prev, fieldName: e.target.value }))}
                          placeholder={`e.g., annual_contract_value${getCustomSuffix()}`}
                          helpText={`Used in API calls. Must be lowercase with underscores. Suffix ${getCustomSuffix()} will be added automatically if not present.`}
                        />
                      </div>
                      <span className="text-gray-500 dark:text-gray-400 text-sm mt-6">{getCustomSuffix()}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <StandardTextarea
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this field is used for..."
                    rows={2}
                  />

                  {/* Help Text */}
                  <StandardInput
                    label="Help Text"
                    value={formData.helpText}
                    onChange={(e) => setFormData(prev => ({ ...prev, helpText: e.target.value }))}
                    placeholder="Tooltip text shown to users..."
                  />
                </div>
              </div>

              {/* Field Type Specific Settings */}
              {fieldType === 'text' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Text Field Settings</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Maximum Length
                    </label>
                    <input
                      type="number"
                      value={formData.length}
                      onChange={(e) => setFormData(prev => ({ ...prev, length: parseInt(e.target.value) }))}
                      min={1}
                      max={255}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum: 255 characters</p>
                  </div>
                </div>
              )}

              {fieldType === 'textarea' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Text Area Settings</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Maximum Length
                    </label>
                    <input
                      type="number"
                      value={formData.length}
                      onChange={(e) => setFormData(prev => ({ ...prev, length: parseInt(e.target.value) }))}
                      min={1}
                      max={1000}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum: 1,000 characters</p>
                  </div>
                </div>
              )}

              {fieldType === 'longtextarea' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Long Text Area Settings</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Maximum Length
                    </label>
                    <input
                      type="number"
                      value={formData.length}
                      onChange={(e) => setFormData(prev => ({ ...prev, length: parseInt(e.target.value) }))}
                      min={256}
                      max={32000}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum: 32,000 characters</p>
                  </div>
                </div>
              )}

              {fieldType === 'richtextarea' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Rich Text Area Settings</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Rich text fields support formatting like <strong>bold</strong>, <em>italic</em>,
                      links, and basic HTML. Users will see a formatting toolbar when editing.
                    </p>
                  </div>
                </div>
              )}

              {fieldType === 'autonumber' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Auto-Number Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Display Format
                      </label>
                      <input
                        type="text"
                        value={formData.autoNumberFormat}
                        onChange={(e) => setFormData(prev => ({ ...prev, autoNumberFormat: e.target.value }))}
                        placeholder="e.g., INV-{0000} or CASE-{00000}"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Use {"{0000}"} for 4-digit number, {"{00000}"} for 5-digit, etc.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Starting Number
                      </label>
                      <input
                        type="number"
                        value={formData.autoNumberStarting}
                        onChange={(e) => setFormData(prev => ({ ...prev, autoNumberStarting: parseInt(e.target.value) }))}
                        min={1}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                      <p className="text-sm text-yellow-800 dark:text-yellow-300">
                        <strong>Preview:</strong> {formData.autoNumberFormat?.replace(/\{0+\}/, '0001') || 'AUTO-0001'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {fieldType === 'time' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Time Settings</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Time fields store time values in 24-hour format (HH:MM).
                      Users can select time using a time picker.
                    </p>
                  </div>
                </div>
              )}

              {fieldType === 'masterdetail' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Master-Detail Relationship Settings</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Master-Detail:</strong> Creates a tight parent-child relationship.
                      Deleting the parent record will delete all child records.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      {isLoadingObjects ? (
                        <>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Master (Parent) Object <span className="text-red-500">*</span>
                          </label>
                          <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                            Loading objects...
                          </div>
                        </>
                      ) : (
                        <AsyncSearchableSelect
                          label="Master (Parent) Object"
                          name="masterDetailLookupObject"
                          value={formData.lookupObject}
                          onChange={(v) => setFormData(prev => ({ ...prev, lookupObject: v }))}
                          onSearch={searchObjectTypes}
                          placeholder="Search master object..."
                          required
                          minSearchLength={0}
                          selectedOptionLabel={lookupTargetObjects.find(o => o.apiName === formData.lookupObject)?.label}
                        />
                      )}
                      {lookupTargetObjects.length === 0 && !isLoadingObjects && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          No other objects available for master-detail relationship.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.masterDetailCascadeDelete}
                          onChange={(e) => setFormData(prev => ({ ...prev, masterDetailCascadeDelete: e.target.checked }))}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Cascade Delete - Delete child records when parent is deleted
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {fieldType === 'formula' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Formula Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Return Type
                      </label>
                      <select
                        value={formData.formulaReturnType}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          formulaReturnType: e.target.value as any
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="currency">Currency</option>
                        <option value="percent">Percent</option>
                        <option value="date">Date</option>
                        <option value="checkbox">Checkbox (True/False)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Formula Expression
                      </label>
                      <textarea
                        value={formData.formulaExpression}
                        onChange={(e) => setFormData(prev => ({ ...prev, formulaExpression: e.target.value }))}
                        placeholder={`e.g., annual_contract_value${getCustomSuffix()} * 0.15`}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Use field API names and operators: +, -, *, /, IF(), AND(), OR()
                      </p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        <strong>Example formulas:</strong>
                      </p>
                      <ul className="text-sm text-blue-800 dark:text-blue-300 mt-2 space-y-1 list-disc list-inside">
                        <li><code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">field1{getCustomSuffix()} + field2{getCustomSuffix()}</code> - Add two numbers</li>
                        <li><code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">IF(amount{getCustomSuffix()} &gt; 1000, "High", "Low")</code> - Conditional</li>
                        <li><code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">TODAY() + 30</code> - Date calculation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {fieldType === 'rollup' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Roll-Up Summary Settings</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Roll-Up Summary:</strong> Calculates values from related child records.
                      Example: Total value of all invoices for a client.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Summarize Child Object
                      </label>
                      <select
                        value={formData.rollupChildObject}
                        onChange={(e) => setFormData(prev => ({ ...prev, rollupChildObject: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select child object...</option>
                        <option value="Invoice__c">Invoice</option>
                        <option value="Case__c">Case</option>
                        <option value="Contact__c">Contact</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Child Field to Aggregate
                      </label>
                      <input
                        type="text"
                        value={formData.rollupChildField}
                        onChange={(e) => setFormData(prev => ({ ...prev, rollupChildField: e.target.value }))}
                        placeholder={`e.g., amount${getCustomSuffix()}`}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Roll-Up Type
                      </label>
                      <select
                        value={formData.rollupOperation}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          rollupOperation: e.target.value as any
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="SUM">SUM - Total of all values</option>
                        <option value="COUNT">COUNT - Number of records</option>
                        <option value="MIN">MIN - Minimum value</option>
                        <option value="MAX">MAX - Maximum value</option>
                        <option value="AVG">AVG - Average value</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {fieldType === 'geolocation' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Geolocation Settings</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Geolocation fields store latitude and longitude coordinates.
                      Users can enter coordinates manually or use their device's location.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Decimal Places
                    </label>
                    <input
                      type="number"
                      value={formData.scale}
                      onChange={(e) => setFormData(prev => ({ ...prev, scale: parseInt(e.target.value) }))}
                      min={1}
                      max={8}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Recommended: 6 decimal places (~10cm accuracy)
                    </p>
                  </div>
                </div>
              )}

              {(fieldType === 'number' || fieldType === 'currency' || fieldType === 'percent') && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Number Settings</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Precision (Total Digits)
                      </label>
                      <input
                        type="number"
                        value={formData.precision}
                        onChange={(e) => setFormData(prev => ({ ...prev, precision: parseInt(e.target.value) }))}
                        min={1}
                        max={18}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Scale (Decimal Places)
                      </label>
                      <input
                        type="number"
                        value={formData.scale}
                        onChange={(e) => setFormData(prev => ({ ...prev, scale: parseInt(e.target.value) }))}
                        min={0}
                        max={17}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}

              {(fieldType === 'picklist' || fieldType === 'multipicklist') && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Picklist Values</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Values (one per line)
                    </label>
                    <textarea
                      value={formData.picklistValues.join('\n')}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        // Don't filter while typing - allow newlines to exist
                        // Filtering happens on save in handleSubmit
                        picklistValues: e.target.value.split('\n')
                      }))}
                      onKeyDown={(e) => {
                        // Allow Enter key to create new lines
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          // Don't preventDefault - we WANT the newline!
                        }
                      }}
                      placeholder="Active&#10;Inactive&#10;Pending"
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                      style={{ resize: 'vertical', whiteSpace: 'pre-wrap' }}
                    />
                  </div>
                </div>
              )}

              {fieldType === 'lookup' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Lookup Settings</h3>
                  <div>
                    {isLoadingObjects ? (
                      <>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Related Object <span className="text-red-500">*</span>
                        </label>
                        <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                          Loading objects...
                        </div>
                      </>
                    ) : (
                      <AsyncSearchableSelect
                        label="Related Object"
                        name="lookupObject"
                        value={formData.lookupObject}
                        onChange={(v) => setFormData(prev => ({ ...prev, lookupObject: v }))}
                        onSearch={searchObjectTypes}
                        placeholder="Search related object..."
                        required
                        minSearchLength={0}
                        selectedOptionLabel={lookupTargetObjects.find(o => o.apiName === formData.lookupObject)?.label}
                      />
                    )}
                    {lookupTargetObjects.length === 0 && !isLoadingObjects && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        No other objects available for lookup relationship.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Field Options */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Field Options</h3>
                <div className="space-y-3">
                  <StandardCheckbox
                    checked={formData.required}
                    onChange={(checked) => setFormData(prev => ({ ...prev, required: checked }))}
                    label="Required"
                    helpText="Users must fill out this field"
                  />

                  <StandardCheckbox
                    checked={formData.unique}
                    onChange={(checked) => setFormData(prev => ({ ...prev, unique: checked }))}
                    label="Unique"
                    helpText="No duplicate values allowed"
                  />

                  <StandardCheckbox
                    checked={formData.isPII}
                    onChange={(checked) => setFormData(prev => ({ ...prev, isPII: checked }))}
                    label="PII"
                    helpText="Contains personally identifiable information"
                  />
                </div>
              </div>

              {/* Default Value */}
              {fieldType !== 'checkbox' && fieldType !== 'lookup' && fieldType !== 'masterdetail' && fieldType !== 'formula' && fieldType !== 'rollup' && fieldType !== 'autonumber' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Default Value</h3>
                  <StandardInput
                    value={formData.defaultValue}
                    onChange={(e) => setFormData(prev => ({ ...prev, defaultValue: e.target.value }))}
                    placeholder="Optional default value..."
                  />
                </div>
              )}

              {/* Field Level Security */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                {formData.id ? (
                  // Existing field - show full security manager
                  <FieldSecurityManager
                    fieldId={formData.id}
                    fieldLabel={formData.fieldLabel || 'New Field'}
                    objectName={objectName}
                    mode="compact"
                  />
                ) : (
                  // New field - show info message
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Field-Level Security
                      </h4>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800 dark:text-blue-300">
                          <p className="font-semibold mb-1">Security settings available after creation</p>
                          <p>
                            Field-level permissions can be configured after this field is created.
                            By default, all roles will have view and edit access.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800">
          {step === 2 && (
            <StandardButton
              variant="ghost"
              onClick={() => setStep(1)}
            >
              ← Back
            </StandardButton>
          )}
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <StandardButton
              variant="secondary"
              onClick={() => {
                onClose();
                resetForm();
              }}
            >
              Cancel
            </StandardButton>
            {step === 2 && (
              <StandardButton
                variant="primary"
                onClick={handleSubmit}
                disabled={!formData.fieldLabel || !formData.fieldName || createFieldMutation.isPending}
                isLoading={createFieldMutation.isPending}
              >
                {createFieldMutation.isPending ? 'Creating...' : 'Create Field'}
              </StandardButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
